import StateController from 'panel/build/component-utils/state-controller';
import {lexicalCompose} from '../../util/function';
import {fuzzyMatch} from '../../util/fuzzy-match';
import {NAVIGATION_KEY_CODES} from '../../util/dom';

const PROGRESSIVE_SIZE = 100; // Render 100 items at first, as user scrolls, render more items as needed

export const SHOW_ALL_ITEM = {
  icon: `caret-down`,
  isExpander: true,
  label: `Show all`,
};

export const SELECT_ALL_ITEM = {
  isSelectAll: true,
  label: `Select all`,
  searchable: false,
};

export class ItemsMenuController extends StateController {
  // Passing explicit dependencies that controller can use
  constructor({dispatchCustomEvent, store = null}) {
    super({store});
    this.dispatchCustomEvent = dispatchCustomEvent;
  }

  get defaultState() {
    return {
      searchExact: false,
      searchFilter: null,
      emptyMessage: null,
      sections: [],
      isProgressive: true, // progressive is true by default because we want dropdowns to be fast by default
      progressiveListSize: PROGRESSIVE_SIZE,
      focusedItemIndex: 0,
      isPillFocused: false,
      sectionFromInputFn: null,
      sectionsHint: null,
      fullyShownSections: {}, // Map to track which sections have 'show all' clicked
      multiple: false,
      showSelectAll: false,
      selectionState: `none`,
    };
  }

  handleAttributeChange(name, newVal) {
    switch (name) {
      case `search-exact`:
        this._update({searchExact: newVal});
        break;
      case `search-filter`:
        this._update({searchFilter: newVal});
        break;
      case `empty-message`:
        this._update({emptyMessage: newVal});
        break;
      case `progressive`:
        this._update({isProgressive: newVal});
        break;
      case `sections-hint`:
        this._update({sectionsHint: newVal});
        break;
      case `multiple`:
        this._update({multiple: newVal});
        break;
      case `sections`:
        this._update({
          sections: newVal,
          selectionState: this.getSelectionState(newVal),
        });
        break;
      case `show-select-all`:
        this._update({showSelectAll: newVal});
        break;
    }
  }

  handlePostPatch() {
    // When this component is patched, the height of it could change. Send a draw event so users of the
    // component can respond to the height change accordingly.
    this.dispatchCustomEvent(`draw`);
  }

  handleMouseenterPill() {
    this._update({isPillFocused: true});
  }

  handleMouseleavePill() {
    this._update({isPillFocused: false});
  }

  handleMouseenterItem(item) {
    this._update({isPillFocused: false});
    this.focusItem(item.index);
  }

  handleClickItem(item, modifierKeys) {
    const {selectionState} = this.state;
    const eventDetail = {
      selectionState,
      modifierKeys,
    };

    if (item.isSelectAll) {
      this.dispatchCustomEvent(`selectAll`, {detail: eventDetail});
    } else if (item.isExpander) {
      this.handleShowAllItemClick(item);
    } else {
      this.dispatchCustomEvent(`clickItem`, {
        detail: {
          ...eventDetail,
          item: this.unwrapItem(item),
        },
      });
    }
  }

  handleClickPill(item, modifierKeys) {
    const detail = {item: this.unwrapItem(item), modifierKeys};
    this.dispatchCustomEvent(`clickPill`, {detail});
  }

  handleShowAllItemClick(item) {
    const sectionIndex = item.sectionIndex;
    const fullyShownSections = this.state.fullyShownSections;
    fullyShownSections[sectionIndex] = true;
    this._update(fullyShownSections);
  }

  handleKeydown(keyCode, modifierKeys) {
    if (keyCode === NAVIGATION_KEY_CODES.TAB) {
      this._update({isPillFocused: !this.state.isPillFocused});
    } else if (keyCode === NAVIGATION_KEY_CODES.ENTER) {
      // user can press enter even though are no items or search results are empty
      const focusedItem = this.getFocusedItem();
      if (focusedItem && !focusedItem.isDisabled) {
        if (this.state.isPillFocused) {
          this.handleClickPill(focusedItem, modifierKeys);
        } else {
          this.handleClickItem(focusedItem, modifierKeys);
        }
      }
    } else if (keyCode === NAVIGATION_KEY_CODES.UP_ARROW) {
      this.focusItem(Math.max(this.state.focusedItemIndex - 1, 0));
    } else if (keyCode === NAVIGATION_KEY_CODES.DOWN_ARROW) {
      this.focusItem(
        Math.min(
          this.state.focusedItemIndex + 1,
          this.countTotalItems(this.getFilteredSections(this.state.sections)) - 1,
        ),
      );
    }
  }

  focusItem(focusedItemIndex) {
    this._update({focusedItemIndex});
    this.dispatchCustomEvent(`focusItem`, {detail: {item: this.getFocusedItem(focusedItemIndex)}});
  }

  getFocusedItemIndex() {
    return this.state.focusedItemIndex;
  }

  getFocusedItem(focusedItemIndex = this.state.focusedItemIndex) {
    const focusedItem = this.getFilteredSections(this.state.sections)
      .reduce((items, section) => [...items, ...section.items], [])
      .find((item) => item.index === focusedItemIndex);
    return focusedItem || null;
  }

  wrapItem(item) {
    return {...{wrapped: item}, ...item};
  }

  unwrapItem(item) {
    return item.wrapped;
  }

  getDisplayState() {
    return {
      emptyMessage: this.state.emptyMessage,
      focusedItemIndex: this.state.focusedItemIndex,
      isPillFocused: this.state.isPillFocused,
      searchFilter: this.state.searchFilter,
      sections: this.getDisplaySections(),
      sectionsHint: this.state.sectionsHint,
    };
  }

  getSelectionState(sections) {
    const totalCount = this.countTotalItems(sections);
    const selectedCount = sections.reduce(
      (count, section) => count + section.items.filter((item) => item.isSelected).length,
      0,
    );

    let state = ``;
    switch (selectedCount) {
      case 0:
        state = `none`;
        break;
      case totalCount:
        state = `all`;
        break;
      default:
        state = `some`;
        break;
    }

    return state;
  }

  getDisplaySections() {
    let sections = this.state.sections;
    const isProgressive = this.state.isProgressive;
    sections = this.getFilteredSections(sections);
    sections = isProgressive ? this.getProgressiveCappedSections(sections) : sections;
    sections = this.getFilledSections(sections);
    return sections;
  }

  getFilteredSections(sections) {
    const {searchFilter} = this.state;

    // Pass sections through a bunch of pure transforms and filter functions
    sections = this.getWrappedSections(sections);
    if (searchFilter) {
      sections = this.getMatchedSections(searchFilter, sections);
      sections = this.appendSectionFromInput(searchFilter, sections);
    } else {
      sections = this.appendSelectAllSection(sections);
    }
    sections = this.getShowLimitCappedSections(sections);
    sections = this.getItemIndexedSections(sections);

    return sections;
  }

  // Append section only if there is no exact match
  canAppendSectionFromInput(searchFilter, sections) {
    for (const section of sections) {
      for (const item of section.items) {
        if (item.label === searchFilter) {
          return false; // exact match, item already exists, terminate early.
        }
      }
    }
    return true;
  }

  appendSectionFromInput(searchFilter, sections) {
    const sectionFromInputFn = this.state.sectionFromInputFn;
    const newSection = sectionFromInputFn && sectionFromInputFn(searchFilter);
    if (newSection && this.canAppendSectionFromInput(searchFilter, sections)) {
      return sections.concat(this.getWrappedSections([newSection]));
    }
    return sections;
  }

  // Adds a virtual section or item for a select all option when in multiple mode
  appendSelectAllSection(sections) {
    const {multiple, showSelectAll, selectionState} = this.state;
    const totalCount = this.countTotalItems(sections);
    const selectAllItem = {
      ...SELECT_ALL_ITEM,
      isSelected: selectionState === `all`,
    };

    if (multiple && showSelectAll && totalCount > 0) {
      sections = [...sections];

      // if there is only a single section, then 'Select All' is added as the first
      // item, otherwise 'Select All' is added as the first section on its own.
      if (sections.length === 1) {
        sections[0] = {
          ...sections[0],
          items: [selectAllItem, ...sections[0].items],
        };
      } else {
        sections.unshift({items: [selectAllItem]});
      }
    }

    return sections;
  }

  getWrappedSections(sections) {
    return sections.map((section) => ({...section, items: section.items.map((item) => this.wrapItem(item))}));
  }

  getMatchedSections(searchFilter, sections) {
    return sections.map((section) => ({...section, items: this.getMatchingItems(searchFilter, section.items)}));
  }

  getShowLimitCappedSections(sections) {
    // If there is only one section, don't add 'show all' since the user will already need to scroll
    // it adds an extra click to UX which is unnecessary
    if (sections.length <= 1) {
      return sections;
    }

    const fullyShownSections = this.state.fullyShownSections;
    return sections.map((section, idx) => {
      const showLimit = section.showLimit;
      let items = section.items;
      // Add a `show all (x)` item at the end of section and limit number of items displayed
      if (showLimit > 0 && items.length > showLimit && !fullyShownSections[idx]) {
        const showAllItem = {...SHOW_ALL_ITEM, sectionIndex: idx, label: `${SHOW_ALL_ITEM.label} (${items.length})`};
        items = items.slice(0, showLimit).concat([showAllItem]);
      }
      return {...section, items};
    });
  }

  getProgressiveCappedSections(sections) {
    const progressiveListSize = this.state.progressiveListSize;
    return sections.map((section) => ({
      ...section,
      items: section.items.filter((item) => item.index < progressiveListSize),
    }));
  }

  getItemIndexedSections(sections) {
    let index = 0;
    return sections.map((section) => ({
      ...section,
      items: section.items.map((item) => ({...item, index: index++})),
    }));
  }

  getFilledSections(sections) {
    const searchFilter = this.state.searchFilter;
    // Loading section only shown in non search scenarios
    return sections.filter((section) => section.items.length > 0 || (!searchFilter && section.isLoading));
  }

  countTotalItems(sections) {
    return sections.reduce((numItems, section) => numItems + section.items.length, 0);
  }

  sortOnProperty(matchProp) {
    const getMatch = (item) => (item[matchProp] ? item[matchProp] : {score: -1, matchStr: ``});
    return lexicalCompose(
      // fuzzy match score
      (a, b) => getMatch(b).score - getMatch(a).score,
      // alphabetical
      (a, b) => getMatch(a).matchStr.localeCompare(getMatch(b).matchStr),
    );
  }

  getMatchingItems(searchFilter, items) {
    if (this.state.searchExact) {
      searchFilter = `"` + searchFilter + `"`;
    }
    return items
      .map((item) => ({
        ...item,
        labelMatch: fuzzyMatch(item.label, searchFilter),
        subLabelMatch: fuzzyMatch(item.subLabel, searchFilter),
      }))
      .filter((item) => !!item.labelMatch || !!item.subLabelMatch)
      .sort(lexicalCompose(this.sortOnProperty(`labelMatch`), this.sortOnProperty(`subLabelMatch`)));
  }

  increaseProgressiveListSize() {
    const isProgressive = this.state.isProgressive;
    let progressiveListSize = this.state.progressiveListSize;
    if (isProgressive && progressiveListSize < this.countTotalItems(this.getFilteredSections(this.state.sections))) {
      progressiveListSize += PROGRESSIVE_SIZE;
      this._update({progressiveListSize});
    }
  }
}
